import { useMemo } from 'react';
import {
    Box,
    CircularProgress,
    Paper,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import DeveloperBoardIcon from '@material-ui/icons/DeveloperBoard';
import BrokenImageOutlinedIcon from '@material-ui/icons/BrokenImageOutlined';
import GrainIcon from '@mui/icons-material/Grain';
import TimelineIcon from '@material-ui/icons/Timeline';

const useStyles = makeStyles(theme => ({
    section: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.background.default,
        boxShadow: theme.palette.shadow.card,
        overflow: 'hidden',
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexShrink: 0,
        padding: theme.spacing(2.5, 2.5, 2),
        '@media (max-width: 700px)': {
            padding: theme.spacing(2, 2, 1.5),
        },
    },
    sectionHeaderLeft: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        minWidth: 0,
    },
    sectionHeaderActions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    sectionIcon: {
        width: 40,
        height: 40,
        minWidth: 40,
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    sectionIconDiagram: {
        background: theme.palette.surface.selected,
        color: theme.palette.primary.main,
    },
    sectionIconAutomate: {
        background: theme.palette.status.successPanel,
        color: theme.palette.status.success,
    },
    sectionTitle: {
        color: theme.palette.text.primary,
        fontSize: 18,
        fontWeight: 700,
        lineHeight: 1.2,
        marginBottom: theme.spacing(0.5),
    },
    sectionSubtitle: {
        color: theme.palette.text.secondary,
        fontSize: 13,
        lineHeight: 1.45,
    },
    sectionContent: {
        flex: 1,
        minHeight: 0,
        ...theme.scrollbars.scrollsSettings,
        overflowY: 'scroll',
        marginBottom: 0,
        '&::-webkit-scrollbar': {
            ...theme.scrollbars.webkitScrollbar,
        },
        '&::-webkit-scrollbar-track': {
            ...theme.scrollbars.webkitScrollbarTrack,
        },
        '&::-webkit-scrollbar-thumb': {
            background: theme.palette.primary.main,
        },
    },
    sectionContentInner: {
        padding: theme.spacing(0, 2.5, 3),
        '@media (max-width: 700px)': {
            padding: theme.spacing(0, 2, 2),
        },
    },
    diagramsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: theme.spacing(1.75),
        alignContent: 'start',
        '@media (max-width: 1680px)': {
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        },
        '@media (max-width: 1380px)': {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        '@media (max-width: 620px)': {
            gridTemplateColumns: '1fr',
        },
    },
    diagramCard: {
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.main}`,
        backgroundColor: theme.palette.background.default,
        boxShadow: theme.palette.shadow.card,
        overflow: 'hidden',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.palette.shadow.sticky,
            borderColor: theme.palette.border.soft,
        },
    },
    diagramCardSelectable: {
        cursor: 'pointer',
    },
    diagramCardSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.surface.selected,
        boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
    diagramPreview: {
        position: 'relative',
        height: 96,
        margin: theme.spacing(1.25, 1.25, 1),
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        backgroundColor: theme.palette.surface.soft,
        border: `1px solid ${theme.palette.border.soft}`,
        overflow: 'hidden',
        cursor: 'pointer',
        '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: `radial-gradient(circle, ${theme.palette.surface.accentBorder} 1px, transparent 1px)`,
            backgroundSize: '12px 12px',
            opacity: 0.35,
        },
    },
    diagramPreviewAutomate: {
        margin: theme.spacing(1.25, 1.25, 1),
        height: 96,
        backgroundColor: '#f0fdf4c4',
        border: `1px solid ${theme.palette.status.successPanel}`,
        '&::before': {
            backgroundImage: 'radial-gradient(circle, rgba(74, 222, 128, 0.28) 1px, transparent 1px)',
        },
    },
    diagramPreviewMissing: {
        backgroundColor: 'rgba(245, 158, 11, 0.11)',
        border: `1px solid rgba(245, 158, 11, 0.28)`,
        '&::before': {
            display: 'none',
        },
    },
    diagramPreviewUnsaved: {
        backgroundColor: 'rgba(125, 211, 252, 0.14)',
        border: '1px solid rgba(125, 211, 252, 0.34)',
        '&::before': {
            display: 'none',
        },
    },
    diagramPreviewMissingContent: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        zIndex: 2,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.8),
        padding: theme.spacing(0.8, 1.2),
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        backgroundColor: 'rgba(245, 158, 11, 0.12)',
        border: `1px solid rgba(245, 158, 11, 0.34)`,
        color: theme.palette.warning.dark,
        boxShadow: '0 6px 14px rgba(245, 158, 11, 0.08)',
        whiteSpace: 'nowrap',
    },
    diagramPreviewUnsavedContent: {
        backgroundColor: 'rgba(186, 230, 253, 0.3)',
        border: '1px solid rgba(125, 211, 252, 0.42)',
        color: '#0369a1',
        boxShadow: '0 6px 14px rgba(125, 211, 252, 0.1)',
    },
    diagramPreviewMissingIcon: {
        fontSize: 18,
        color: theme.palette.warning.dark,
    },
    diagramPreviewUnsavedIcon: {
        color: '#0284c7',
    },
    diagramPreviewMissingText: {
        fontSize: 11.5,
        fontWeight: 700,
        lineHeight: 1,
    },
    diagramPreviewMissingLine: {
        position: 'absolute',
        width: 72,
        height: 1.4,
        backgroundColor: theme.palette.text.disabled,
        zIndex: 1,
    },
    diagramPreviewContent: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        zIndex: 1,
        width: 'calc(100% - 8px)',
        height: 'calc(100% - 8px)',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    diagramPreviewLoading: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        zIndex: 2,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    diagramPreviewImage: {
        width: '100%',
        height: '100%',
        display: 'block',
        objectFit: 'contain',
    },
    diagramCardBody: {
        padding: theme.spacing(0, 1.35, 1.15),
    },
    diagramName: {
        color: theme.palette.text.primary,
        fontSize: 12.5,
        fontWeight: 700,
        lineHeight: 1.35,
        minHeight: 32,
        marginBottom: theme.spacing(0.8),
        display: '-webkit-box',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    metaRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.65),
        color: theme.palette.text.disabled,
        fontSize: 10.5,
        lineHeight: 1.4,
    },
    metaBlock: {
        marginTop: theme.spacing(0.15),
    },
    metaLabel: {
        color: theme.palette.text.secondary,
        fontSize: 10.5,
        fontWeight: 700,
        lineHeight: 1.35,
        marginBottom: theme.spacing(0.2),
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    },
    automateList: {
        display: 'grid',
        gap: theme.spacing(1),
        alignContent: 'start',
    },
    automateCard: {
        display: 'grid',
        gridTemplateColumns: '104px minmax(0, 1fr) 220px',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(0.6, 1.45),
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.main}`,
        borderLeft: `4px solid ${theme.palette.status.online}`,
        backgroundColor: theme.palette.background.default,
        boxShadow: theme.palette.shadow.card,
        cursor: 'pointer',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.palette.shadow.sticky,
            borderColor: theme.palette.border.soft,
        },
        '@media (max-width: 620px)': {
            gridTemplateColumns: '1fr',
            justifyItems: 'start',
        },
    },
    automateIconWrap: {
        width: 94,
        height: 94,
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        backgroundColor: theme.palette.status.successPanel,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.text.secondary,
    },
    automateContent: {
        minWidth: 0,
    },
    automateName: {
        color: theme.palette.text.primary,
        fontSize: 15,
        fontWeight: 700,
        lineHeight: 1.35,
        marginBottom: theme.spacing(0.7),
    },
    automateThumb: {
        width: 220,
        minHeight: 0,
        justifySelf: 'end',
        '@media (max-width: 620px)': {
            justifySelf: 'start',
        },
    },
    emptyState: {
        padding: theme.spacing(3),
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px dashed ${theme.palette.border.main}`,
        backgroundColor: theme.palette.surface.soft,
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    previewNode: {
        fill: theme.palette.background.default,
        stroke: theme.palette.primary.main,
        strokeWidth: 2,
    },
    previewNodeAutomate: {
        fill: theme.palette.background.default,
        stroke: '#52b788',
        strokeWidth: 2,
    },
    previewNodeWide: {
        fill: theme.palette.background.default,
        stroke: theme.palette.primary.main,
        strokeWidth: 2,
    },
    previewLine: {
        fill: 'none',
        stroke: theme.palette.primary.main,
        strokeWidth: 1.2,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
    },
    previewLineAutomate: {
        fill: 'none',
        stroke: '#52b788',
        strokeWidth: 1.2,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
    },
    previewMark: {
        stroke: theme.palette.primary.light,
        strokeWidth: 1,
        strokeLinecap: 'round',
    },
    previewMarkAutomate: {
        stroke: '#74c69d',
        strokeWidth: 1,
        strokeLinecap: 'round',
    },
    previewPort: {
        fill: theme.palette.background.default,
        stroke: theme.palette.primary.main,
        strokeWidth: 1,
    },
    previewPortAutomate: {
        fill: theme.palette.background.default,
        stroke: '#52b788',
        strokeWidth: 1,
    },
    previewSvg: {
        width: '100%',
        height: '100%',
    },
}));

const THUMBNAIL_BLOCK_SIZE = 16;
const DIAGRAM_VARIANTS = [
    {
        blocks: [
            { id: 'a', x: 8, y: 30 },
            { id: 'b', x: 56, y: 14 },
            { id: 'c', x: 56, y: 54 },
            { id: 'd', x: 108, y: 14 },
            { id: 'e', x: 108, y: 54 },
            { id: 'f', x: 168, y: 14 },
            { id: 'g', x: 168, y: 54 },
        ],
        connections: [
            { points: [{ ref: 'a', side: 'right' }, { x: 40, y: 38 }, { x: 40, y: 22 }, { ref: 'b', side: 'left' }] },
            { points: [{ ref: 'a', side: 'right' }, { x: 40, y: 38 }, { x: 40, y: 62 }, { ref: 'c', side: 'left' }] },
            { points: [{ ref: 'b', side: 'right' }, { x: 92, y: 22 }, { ref: 'd', side: 'left' }] },
            { points: [{ ref: 'c', side: 'right' }, { x: 92, y: 62 }, { ref: 'e', side: 'left' }] },
            { points: [{ ref: 'd', side: 'right' }, { x: 152, y: 22 }, { ref: 'f', side: 'left' }] },
            { points: [{ ref: 'e', side: 'right' }, { x: 152, y: 62 }, { ref: 'g', side: 'left' }] },
        ],
    },
    {
        blocks: [
            { id: 'a', x: 8, y: 18 },
            { id: 'b', x: 52, y: 18 },
            { id: 'c', x: 96, y: 18 },
            { id: 'd', x: 96, y: 52 },
            { id: 'e', x: 156, y: 18 },
            { id: 'f', x: 156, y: 52 },
        ],
        connections: [
            { points: [{ ref: 'a', side: 'right' }, { x: 44, y: 26 }, { ref: 'b', side: 'left' }] },
            { points: [{ ref: 'b', side: 'right' }, { x: 88, y: 26 }, { ref: 'c', side: 'left' }] },
            { points: [{ ref: 'b', side: 'right' }, { x: 74, y: 26 }, { x: 74, y: 60 }, { ref: 'd', side: 'left' }] },
            { points: [{ ref: 'c', side: 'right' }, { x: 140, y: 26 }, { ref: 'e', side: 'left' }] },
            { points: [{ ref: 'd', side: 'right' }, { x: 140, y: 60 }, { ref: 'f', side: 'left' }] },
        ],
    },
    {
        blocks: [
            { id: 'a', x: 8, y: 14 },
            { id: 'b', x: 8, y: 56 },
            { id: 'c', x: 64, y: 35 },
            { id: 'd', x: 108, y: 18 },
            { id: 'e', x: 108, y: 52 },
            { id: 'f', x: 168, y: 18 },
            { id: 'g', x: 168, y: 52 },
        ],
        connections: [
            { points: [{ ref: 'a', side: 'right' }, { x: 44, y: 22 }, { x: 44, y: 43 }, { ref: 'c', side: 'left' }] },
            { points: [{ ref: 'b', side: 'right' }, { x: 44, y: 64 }, { x: 44, y: 43 }, { ref: 'c', side: 'left' }] },
            { points: [{ ref: 'c', side: 'right' }, { x: 92, y: 43 }, { x: 92, y: 26 }, { ref: 'd', side: 'left' }] },
            { points: [{ ref: 'c', side: 'right' }, { x: 92, y: 43 }, { x: 92, y: 60 }, { ref: 'e', side: 'left' }] },
            { points: [{ ref: 'd', side: 'right' }, { x: 152, y: 26 }, { ref: 'f', side: 'left' }] },
            { points: [{ ref: 'e', side: 'right' }, { x: 152, y: 60 }, { ref: 'g', side: 'left' }] },
        ],
    },
    {
        blocks: [
            { id: 'a', x: 8, y: 20 },
            { id: 'b', x: 52, y: 20 },
            { id: 'c', x: 52, y: 54 },
            { id: 'd', x: 96, y: 37 },
            { id: 'e', x: 148, y: 20 },
            { id: 'f', x: 148, y: 54 },
            { id: 'g', x: 192, y: 20 },
            { id: 'h', x: 192, y: 54 },
        ],
        connections: [
            { points: [{ ref: 'a', side: 'right' }, { x: 36, y: 28 }, { ref: 'b', side: 'left' }] },
            { points: [{ ref: 'a', side: 'right' }, { x: 36, y: 28 }, { x: 36, y: 62 }, { ref: 'c', side: 'left' }] },
            { points: [{ ref: 'b', side: 'right' }, { x: 84, y: 28 }, { x: 84, y: 45 }, { ref: 'd', side: 'left' }] },
            { points: [{ ref: 'c', side: 'right' }, { x: 84, y: 62 }, { x: 84, y: 45 }, { ref: 'd', side: 'left' }] },
            { points: [{ ref: 'd', side: 'right' }, { x: 132, y: 45 }, { x: 132, y: 28 }, { ref: 'e', side: 'left' }] },
            { points: [{ ref: 'd', side: 'right' }, { x: 132, y: 45 }, { x: 132, y: 62 }, { ref: 'f', side: 'left' }] },
            { points: [{ ref: 'e', side: 'right' }, { x: 176, y: 28 }, { ref: 'g', side: 'left' }] },
            { points: [{ ref: 'f', side: 'right' }, { x: 176, y: 62 }, { ref: 'h', side: 'left' }] },
        ],
    },
    {
        blocks: [
            { id: 'a', x: 8, y: 34 },
            { id: 'b', x: 56, y: 14 },
            { id: 'c', x: 56, y: 54 },
            { id: 'd', x: 100, y: 14 },
            { id: 'e', x: 100, y: 54 },
            { id: 'f', x: 156, y: 34 },
            { id: 'g', x: 196, y: 34 },
        ],
        connections: [
            { points: [{ ref: 'a', side: 'right' }, { x: 40, y: 42 }, { x: 40, y: 22 }, { ref: 'b', side: 'left' }] },
            { points: [{ ref: 'a', side: 'right' }, { x: 40, y: 42 }, { x: 40, y: 62 }, { ref: 'c', side: 'left' }] },
            { points: [{ ref: 'b', side: 'right' }, { x: 92, y: 22 }, { ref: 'd', side: 'left' }] },
            { points: [{ ref: 'c', side: 'right' }, { x: 92, y: 62 }, { ref: 'e', side: 'left' }] },
            { points: [{ ref: 'd', side: 'right' }, { x: 140, y: 22 }, { x: 140, y: 42 }, { ref: 'f', side: 'left' }] },
            { points: [{ ref: 'e', side: 'right' }, { x: 140, y: 62 }, { x: 140, y: 42 }, { ref: 'f', side: 'left' }] },
            { points: [{ ref: 'f', side: 'right' }, { x: 188, y: 42 }, { ref: 'g', side: 'left' }] },
        ],
    },
    {
        blocks: [
            { id: 'a', x: 8, y: 16 },
            { id: 'b', x: 8, y: 56 },
            { id: 'c', x: 60, y: 36 },
            { id: 'd', x: 104, y: 16 },
            { id: 'e', x: 104, y: 56 },
            { id: 'f', x: 160, y: 36 },
            { id: 'g', x: 196, y: 36 },
        ],
        connections: [
            { points: [{ ref: 'a', side: 'right' }, { x: 42, y: 24 }, { x: 42, y: 44 }, { ref: 'c', side: 'left' }] },
            { points: [{ ref: 'b', side: 'right' }, { x: 42, y: 64 }, { x: 42, y: 44 }, { ref: 'c', side: 'left' }] },
            { points: [{ ref: 'c', side: 'right' }, { x: 88, y: 44 }, { x: 88, y: 24 }, { ref: 'd', side: 'left' }] },
            { points: [{ ref: 'c', side: 'right' }, { x: 88, y: 44 }, { x: 88, y: 64 }, { ref: 'e', side: 'left' }] },
            { points: [{ ref: 'd', side: 'right' }, { x: 148, y: 24 }, { x: 148, y: 44 }, { ref: 'f', side: 'left' }] },
            { points: [{ ref: 'e', side: 'right' }, { x: 148, y: 64 }, { x: 148, y: 44 }, { ref: 'f', side: 'left' }] },
            { points: [{ ref: 'f', side: 'right' }, { x: 188, y: 44 }, { ref: 'g', side: 'left' }] },
        ],
    },
];

const resolveAnchorPoint = (block, side, fraction = 0.5) => {
    const width = block.width || block.size || THUMBNAIL_BLOCK_SIZE;
    const height = block.height || block.size || THUMBNAIL_BLOCK_SIZE;

    if (side === 'left') {
        return { x: block.x, y: block.y + (height * fraction) };
    }
    if (side === 'right') {
        return { x: block.x + width, y: block.y + (height * fraction) };
    }
    if (side === 'top') {
        return { x: block.x + (width * fraction), y: block.y };
    }
    if (side === 'bottom') {
        return { x: block.x + (width * fraction), y: block.y + height };
    }

    return { x: block.x + (width / 2), y: block.y + (height / 2) };
};

const resolveConnectionPoint = (point, blocksMap) => {
    if (point?.ref) {
        return resolveAnchorPoint(blocksMap[point.ref], point.side);
    }

    return point;
};

const buildConnectionPath = (connection, blocksMap) => {
    const points = connection.points.map(point => resolveConnectionPoint(point, blocksMap));

    if (points.length === 0) {
        return '';
    }

    return points.reduce((path, point, index) => {
        const command = index === 0 ? 'M' : 'L';
        return `${path}${index > 0 ? ' ' : ''}${command}${point.x} ${point.y}`;
    }, '');
};

const getAssociation = (item, dataDiagramFreeList = []) => {
    const diagramName = item?.diagram?.name;
    const match = dataDiagramFreeList.find(element => element?.name === diagramName);

    if (match?.id) {
        return {
            linked: true,
            diagramId: match.id,
            label: match.name,
        };
    }

    return {
        linked: false,
        diagramId: null,
        label: diagramName || 'Sin diagrama',
    };
};

const DiagramThumbnail = ({
    classes,
    itemId,
    onClick,
    thumbnailSrc,
    tone = 'diagram',
    missing = false,
    missingText = 'Sin diagrama',
    missingVariant = 'missing',
    isLoading = false,
}) => {
    const numericId = Number(itemId);
    const previewVariant = DIAGRAM_VARIANTS[
        Number.isNaN(numericId) ? 0 : numericId % DIAGRAM_VARIANTS.length
    ];
    const thumbnailBlockRadius = 0;
    const blocksMap = previewVariant.blocks.reduce((accumulator, block) => {
        accumulator[block.id] = block;
        return accumulator;
    }, {});
    const lineClassName = tone === 'automate' ? classes.previewLineAutomate : classes.previewLine;
    const nodeClassName = tone === 'automate' ? classes.previewNodeAutomate : classes.previewNode;
    const markClassName = tone === 'automate' ? classes.previewMarkAutomate : classes.previewMark;
    const portClassName = tone === 'automate' ? classes.previewPortAutomate : classes.previewPort;
    const previewClassName = [
        classes.diagramPreview,
        tone === 'automate' ? classes.diagramPreviewAutomate : '',
        missing ? (missingVariant === 'unsaved' ? classes.diagramPreviewUnsaved : classes.diagramPreviewMissing) : '',
    ].filter(Boolean).join(' ');
    const missingContentClassName = [
        classes.diagramPreviewMissingContent,
        missingVariant === 'unsaved' ? classes.diagramPreviewUnsavedContent : '',
    ].filter(Boolean).join(' ');
    const missingIconClassName = [
        classes.diagramPreviewMissingIcon,
        missingVariant === 'unsaved' ? classes.diagramPreviewUnsavedIcon : '',
    ].filter(Boolean).join(' ');

    return (
        <Box
            className={previewClassName}
            onClick={onClick}
        >
            {isLoading ? (
                <Box className={classes.diagramPreviewLoading}>
                    <CircularProgress
                        size={26}
                        thickness={4.4}
                        style={{
                            color: tone === 'automate' ? '#52b788' : undefined,
                        }}
                    />
                </Box>
            ) : missing ? (
                <Box className={missingContentClassName}>
                    <BrokenImageOutlinedIcon className={missingIconClassName} />
                    <span className={classes.diagramPreviewMissingText}>{missingText}</span>
                </Box>
            ) : thumbnailSrc ? (
                <Box className={classes.diagramPreviewContent}>
                    <img
                        src={thumbnailSrc}
                        alt=''
                        className={classes.diagramPreviewImage}
                        loading='lazy'
                    />
                </Box>
            ) : (
                <Box className={classes.diagramPreviewContent}>
                    <svg
                        viewBox='0 0 220 86'
                        className={classes.previewSvg}
                        aria-hidden='true'
                    >
                        {previewVariant.connections.map((connection, index) => (
                            <path
                                key={`path-${index}`}
                                d={buildConnectionPath(connection, blocksMap)}
                                className={lineClassName}
                            />
                        ))}

                        {previewVariant.blocks.map((block, index) => (
                            <g key={`block-${index}`}>
                                {(() => {
                                    const x = block.x;
                                    const y = block.y;
                                    const width = THUMBNAIL_BLOCK_SIZE;
                                    const height = THUMBNAIL_BLOCK_SIZE;
                                    const midY = y + (height / 2);
                                    return (
                                        <>
                                            <rect
                                                x={x}
                                                y={y}
                                                width={width}
                                                height={height}
                                                rx={thumbnailBlockRadius}
                                                className={nodeClassName}
                                            />
                                            <line
                                                x1={x + 3}
                                                y1={midY - 3}
                                                x2={x + width - 3}
                                                y2={midY - 3}
                                                className={markClassName}
                                            />
                                            <line
                                                x1={x + 3}
                                                y1={midY + 2}
                                                x2={x + width - 6}
                                                y2={midY + 2}
                                                className={markClassName}
                                            />
                                            <circle
                                                cx={x + width}
                                                cy={midY}
                                                r='1.7'
                                                className={portClassName}
                                            />
                                        </>
                                    );
                                })()}
                            </g>
                        ))}
                    </svg>
                </Box>
            )}
        </Box>
    );
};

const DiagramCard = ({
    classes,
    item,
    thumbnailSrc,
    isLoading,
    isSelected = false,
    onSelect,
}) => {
    const isMissingThumbnail = !isLoading && !thumbnailSrc;
    const cardClassName = [
        classes.diagramCard,
        onSelect ? classes.diagramCardSelectable : '',
        isSelected ? classes.diagramCardSelected : '',
    ].filter(Boolean).join(' ');

    return (
        <Paper
            className={cardClassName}
            elevation={0}
            onClick={() => onSelect?.(item)}
            data-diagram-selection-card='true'
        >
            <DiagramThumbnail
                classes={classes}
                itemId={item?.id}
                thumbnailSrc={thumbnailSrc}
                missing={isMissingThumbnail}
                missingText='Sin vista previa'
                missingVariant='unsaved'
                isLoading={isLoading}
            />

            <Box className={classes.diagramCardBody}>
                <Typography className={classes.diagramName}>
                    {item?.nombre || item?.name}
                </Typography>

                <Box className={classes.metaBlock}>
                    <Box className={classes.metaLabel}>
                        Ultima modificacion
                    </Box>
                    <Box className={classes.metaRow}>
                        <CalendarTodayOutlinedIcon style={{ fontSize: 14 }} />
                        <span>{item?.modified_format || 'Sin actualizaciones'}</span>
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
};

const AutomateCard = ({ classes, item, association, onPrimaryAction, thumbnailSrc, isLoading }) => {
    const isMissingThumbnail = !isLoading && !thumbnailSrc;

    return (
        <Paper
            className={classes.automateCard}
            elevation={0}
            onClick={() => onPrimaryAction(item?.id)}
        >
            <Box className={classes.automateIconWrap}>
                <DeveloperBoardIcon style={{ fontSize: 34 }} />
            </Box>

            <Box className={classes.automateContent}>
                <Typography className={classes.automateName}>
                    {item?.nombre || item?.name}
                </Typography>

                <Box className={classes.metaBlock}>
                    <Box className={classes.metaLabel}>
                        Ultima programación
                    </Box>
                    <Box className={classes.metaRow}>
                        <CalendarTodayOutlinedIcon style={{ fontSize: 14 }} />
                        <span>{item?.last_send?.modified_format || 'Sin envios recientes'}</span>
                    </Box>
                </Box>
            </Box>

            <Box className={classes.automateThumb}>
                <Tooltip
                    title={association.linked ? 'Tiene un diagrama asignado' : 'No tiene un diagrama asignado'}
                    arrow
                    placement='top'
                >
                    <Box>
                        <DiagramThumbnail
                            classes={classes}
                            itemId={association?.diagramId || item?.id}
                            thumbnailSrc={thumbnailSrc}
                            tone='automate'
                            missing={!association.linked || isMissingThumbnail}
                            missingText={association.linked ? 'Sin vista previa' : 'Sin diagrama'}
                            missingVariant={association.linked ? 'unsaved' : 'missing'}
                            isLoading={isLoading}
                        />
                    </Box>
                </Tooltip>
            </Box>
        </Paper>
    );
};

const DiagramVisualSection = ({
    type,
    title,
    subtitle,
    items = [],
    previewLimit,
    onPrimaryAction,
    dataDiagramFreeList = [],
    diagramThumbnailMap = {},
    automateThumbnailMap = {},
    thumbnailsLoading = false,
    headerActions = null,
    selectedDiagramIds = [],
    onSelectDiagram,
}) => {
    const classes = useStyles();
    const isAutomate = type === 'automate';

    const visibleItems = useMemo(() => {
        if (!previewLimit) {
            return items;
        }

        return items.slice(0, previewLimit);
    }, [items, previewLimit]);

    return (
        <Paper className={classes.section} elevation={0}>
            <Box className={classes.sectionHeader}>
                <Box className={classes.sectionHeaderLeft}>
                    <Box className={`${classes.sectionIcon} ${isAutomate ? classes.sectionIconAutomate : classes.sectionIconDiagram}`}>
                        {isAutomate ? <GrainIcon /> : <TimelineIcon />}
                    </Box>

                    <Box>
                        <Typography className={classes.sectionTitle}>
                            {title}
                        </Typography>
                        <Typography className={classes.sectionSubtitle}>
                            {subtitle}
                        </Typography>
                    </Box>
                </Box>

                {headerActions ? (
                    <Box className={classes.sectionHeaderActions}>
                        {headerActions}
                    </Box>
                ) : null}
            </Box>

            <Box className={classes.sectionContent}>
                <Box className={classes.sectionContentInner}>
                    {items.length === 0 ? (
                        <Box className={classes.emptyState}>
                            {isAutomate ? 'No hay dispositivos Automate disponibles.' : 'No hay diagramas cargados.'}
                        </Box>
                    ) : (
                        <>
                            {isAutomate ? (
                                <Box className={classes.automateList}>
                                    {visibleItems.map(item => {
                                        const association = getAssociation(item, dataDiagramFreeList);

                                        return (
                                            <AutomateCard
                                                key={item?.id}
                                                classes={classes}
                                                item={item}
                                                association={association}
                                                thumbnailSrc={automateThumbnailMap[item?.id]}
                                                isLoading={association.linked && thumbnailsLoading && !automateThumbnailMap[item?.id]}
                                                onPrimaryAction={onPrimaryAction}
                                            />
                                        );
                                    })}
                                </Box>
                            ) : (
                                <Box className={classes.diagramsGrid}>
                                    {visibleItems.map(item => (
                                        <DiagramCard
                                            key={item?.id}
                                            classes={classes}
                                            item={item}
                                            thumbnailSrc={diagramThumbnailMap[item?.id]}
                                            isLoading={thumbnailsLoading && !diagramThumbnailMap[item?.id]}
                                            isSelected={selectedDiagramIds.includes(item?.id)}
                                            onSelect={onSelectDiagram}
                                        />
                                    ))}
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Paper>
    );
};

export default DiagramVisualSection;
